import React, { useState } from 'react';
import { PersonFinal } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function PersonaFitScoreUploader() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(/[,\t]/).map(h => h.trim());
    
    // Validate headers
    const requiredHeaders = ['tenant_id', 'person_id', 'persona_fit_score'];
    const hasAllHeaders = requiredHeaders.every(h => headers.includes(h));
    
    if (!hasAllHeaders) {
      throw new Error(`CSV must have headers: ${requiredHeaders.join(', ')}`);
    }
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(/[,\t]/).map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      
      // Only process rows with all required fields
      if (row.tenant_id && row.person_id && row.persona_fit_score) {
        data.push({
          tenant_id: row.tenant_id,
          person_id: row.person_id,
          persona_fit_score: parseFloat(row.persona_fit_score)
        });
      }
    }
    
    return data;
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const text = await file.text();
      const updates = parseCSV(text);
      
      console.log(`Processing ${updates.length} persona fit score updates...`);

      // Fetch all people to match by person_id
      const allPeople = await PersonFinal.filter({});
      
      let updated = 0;
      let notFound = 0;
      let errors = 0;

      for (const update of updates) {
        try {
          // Find person by person_id (could be in person_id field or id field)
          const person = allPeople.find(p => 
            p.person_id === update.person_id || p.id === update.person_id
          );

          if (!person) {
            console.warn(`Person not found: ${update.person_id}`);
            notFound++;
            continue;
          }

          // Check if we need to use tenant_overrides
          const needsTenantOverride = person.tenant_id !== update.tenant_id;

          if (needsTenantOverride) {
            // Use tenant_overrides to set the score for this tenant
            const existingOverrides = person.tenant_overrides || {};
            const tenantOverride = existingOverrides[update.tenant_id] || {};
            
            await PersonFinal.update(person.id, {
              tenant_overrides: {
                ...existingOverrides,
                [update.tenant_id]: {
                  ...tenantOverride,
                  persona_fit_score: update.persona_fit_score
                }
              }
            });
          } else {
            // Direct update for matching tenant
            await PersonFinal.update(person.id, {
              persona_fit_score: update.persona_fit_score
            });
          }

          updated++;
          console.log(`✓ Updated ${person.first_name} ${person.last_name} (${person.person_id}): ${update.persona_fit_score}`);
        } catch (err) {
          console.error(`Error updating ${update.person_id}:`, err);
          errors++;
        }
      }

      setResult({
        total: updates.length,
        updated,
        notFound,
        errors
      });

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Persona Fit Score Uploader
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Upload a CSV file with columns: <strong>tenant_id, person_id, persona_fit_score</strong>
          </p>
          <p className="text-xs text-gray-500">
            • Uses tenant_overrides when tenant_id doesn't match person's tenant_id<br />
            • Directly updates persona_fit_score when tenant matches<br />
            • person_id can be the person's ID or person_id field
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isProcessing}
            className="flex-1"
          />
          <Button
            onClick={handleUpload}
            disabled={!file || isProcessing}
            className="whitespace-nowrap"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {result && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <div className="space-y-1">
                <p className="font-semibold">Upload Complete!</p>
                <p className="text-sm">Total rows: {result.total}</p>
                <p className="text-sm">✓ Updated: {result.updated}</p>
                {result.notFound > 0 && <p className="text-sm text-orange-600">⚠ Not found: {result.notFound}</p>}
                {result.errors > 0 && <p className="text-sm text-red-600">✗ Errors: {result.errors}</p>}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}